'use client';

import React, { useState } from 'react';


// ─── SUNNAH PROMPTS PER MODULE ────────────────────────────────────────────────

const SUNNAH_PROMPTS: Record<string, { hadith: string; source: string; reflection: string }> = {
  'irl-lens': {
    hadith: 'Be in this world as if you were a stranger or a traveler along a path.',
    source: 'Bukhari',
    reflection: 'Walking is the Sunnah. The Lens is the reminder.',
  },
  'mrg': {
    hadith: 'The best of people are those most beneficial to people.',
    source: 'Al-Suyuti',
    reflection: 'Service is the Sunnah.',
  },
  'names': {
    hadith: 'Allah has 99 Names — one hundred minus one — and whoever memorizes them will enter Paradise.',
    source: 'Bukhari',
    reflection: 'Recognition is the Sunnah.',
  },
  'quests': {
    hadith: 'The deeds most loved by Allah are those done regularly, even if small.',
    source: 'Bukhari',
    reflection: 'Consistency is the Sunnah.',
  },
  'garment': {
    hadith: 'Taqwah is here — pointing to his chest three times.',
    source: 'Muslim',
    reflection: 'The garment is internal first.',
  },
  'observation-chain': {
    hadith: 'The Prophet ﷺ observed creation with attention. He named things. He asked questions.',
    source: 'Seerah',
    reflection: 'Observation is the Sunnah.',
  },
};

// ─── IBAD AR-RAHMAN TRAITS ────────────────────────────────────────────────────

const IBAD_AR_RAHMAN_TRAITS = [
  { arabic: 'يَمْشُونَ هَوْنًا', english: 'Walks gently on the earth', ref: '25:63' },
  { arabic: 'يَبِيتُونَ لِرَبِّهِمْ سُجَّدًا', english: 'Spends the night prostrating', ref: '25:64' },
  { arabic: 'لَمْ يُسْرِفُوا وَلَمْ يَقْتُرُوا', english: 'Balanced in spending', ref: '25:67' },
  { arabic: 'لَا يَشْهَدُونَ الزُّورَ', english: 'No false witness', ref: '25:72' },
  { arabic: 'مَرُّوا كِرَامًا', english: 'Passes vain talk with dignity', ref: '25:72' },
  { arabic: 'يَدْعُونَ رَبَّهُمُ الرَّحْمَٰنَ', english: 'Calls on Ar-Rahman', ref: '25:65' },
  { arabic: 'يَقُولُونَ سَلَامًا', english: 'Seeks the righteous company', ref: '25:63' },
];

// ─── HADITH OF JIBRIL HORIZON ─────────────────────────────────────────────────

const JIBRIL_LEVELS = [
  { id: 'islam', label: 'Islam', arabic: 'الإسلام', desc: 'Submission — the outer practice', gardenStage: 'Aware', color: '#2d7a47', radius: 120 },
  { id: 'iman', label: 'Iman', arabic: 'الإيمان', desc: 'Faith — the inner conviction', gardenStage: 'Conscious', color: '#2d8a87', radius: 80 },
  { id: 'ihsan', label: 'Ihsan', arabic: 'الإحسان', desc: 'Excellence — to worship Allah as though you see Him', gardenStage: 'Acting', color: '#c9a84c', radius: 40 },
];

// ─── SUNNAH MODULE WIDGET ─────────────────────────────────────────────────────

interface SunnahModuleProps {
  currentStage?: 'aware' | 'conscious' | 'acting';
  activeModuleId?: string;
}

export function SunnahModule({ currentStage = 'aware', activeModuleId }: SunnahModuleProps) {
  const [activeTab, setActiveTab] = useState<'jibril' | 'ibad' | 'sunnah'>('jibril');
  const [expandedTrait, setExpandedTrait] = useState<number | null>(null);

  const currentJibrilLevel = currentStage === 'acting' ? 'ihsan' : currentStage === 'conscious' ? 'iman' : 'islam';
  const activeSunnah = activeModuleId ? SUNNAH_PROMPTS[activeModuleId] : null;

  return (
    <div
      style={{
        background: 'rgba(6,13,18,0.8)',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid rgba(201,168,76,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#c9a84c' }}>الهادي</span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)' }}>
              Al-Hadi · The Guide
            </span>
          </div>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', marginTop: 2 }}>
            Sunnah Module · Awareness Zone
          </p>
        </div>
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#c9a84c',
            boxShadow: '0 0 8px rgba(201,168,76,0.5)',
            animation: 'sunnahPulse 2s ease-in-out infinite',
          }}
        />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { id: 'jibril' as const, label: 'Hadith of Jibril' },
          { id: 'ibad' as const, label: 'Ibad Ar-Rahman' },
          { id: 'sunnah' as const, label: 'Sunnah Prompts' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '8px 4px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #c9a84c' : '2px solid transparent',
              fontFamily: 'monospace',
              fontSize: 8,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.35)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: '16px' }}>

        {/* Hadith of Jibril — Concentric Circles */}
        {activeTab === 'jibril' && (
          <div>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6, marginBottom: 16, fontStyle: 'italic' }}>
              Islam → Iman → Ihsan — the three nested haals. The garden&apos;s ultimate arc.
            </p>
            {/* Concentric circles visualization */}
            <div style={{ position: 'relative', width: '100%', paddingBottom: '60%', marginBottom: 16 }}>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {JIBRIL_LEVELS.slice().reverse().map((level, i) => {
                  const isActive = level.id === currentJibrilLevel;
                  const isHorizon = level.id === 'ihsan';
                  return (
                    <div
                      key={level.id}
                      style={{
                        position: 'absolute',
                        width: `${(level.radius / 120) * 100}%`,
                        paddingBottom: `${(level.radius / 120) * 60}%`,
                        borderRadius: '50%',
                        border: `${isActive ? 2 : 1}px solid ${level.color}${isActive ? 'aa' : '40'}`,
                        background: isActive ? `${level.color}08` : 'transparent',
                        boxShadow: isActive ? `0 0 16px ${level.color}20` : 'none',
                        transition: 'all 0.5s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                        {isHorizon && (
                          <>
                            <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#c9a84c', lineHeight: 1 }}>{level.arabic}</p>
                            <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', whiteSpace: 'nowrap' }}>
                              Horizon
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Level descriptions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {JIBRIL_LEVELS.map(level => {
                const isActive = level.id === currentJibrilLevel;
                return (
                  <div
                    key={level.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: isActive ? `${level.color}08` : 'transparent',
                      border: `1px solid ${isActive ? level.color + '30' : 'transparent'}`,
                      transition: 'all 0.3s',
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: level.color, flexShrink: 0, marginTop: 3 }} />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: level.color }}>{level.arabic}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase', color: `${level.color}80` }}>{level.label}</span>
                        {isActive && (
                          <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '0.08em', textTransform: 'uppercase', color: level.color, background: `${level.color}15`, padding: '1px 6px', borderRadius: 10 }}>
                            Your stage
                          </span>
                        )}
                      </div>
                      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.5 }}>{level.desc}</p>
                      <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${level.color}60`, marginTop: 2 }}>Garden: {level.gardenStage}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
              <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10, lineHeight: 1.6, fontStyle: 'italic' }}>
                &ldquo;Ihsan is to worship Allah as though you see Him, for though you cannot see Him yet He sees you.&rdquo; — Hadith of Jibril
              </p>
            </div>
          </div>
        )}

        {/* Ibad Ar-Rahman */}
        {activeTab === 'ibad' && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: 'rgba(201,168,76,0.7)', direction: 'rtl', marginBottom: 4 }}>
                عِبَادُ الرَّحْمَٰنِ
              </p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
                Servants of the Most Compassionate — Surah Al-Furqan 25:63-76. Not a checklist. A mirror.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {IBAD_AR_RAHMAN_TRAITS.map((trait, i) => (
                <button
                  key={i}
                  onClick={() => setExpandedTrait(expandedTrait === i ? null : i)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: expandedTrait === i ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.01)',
                    border: `1px solid ${expandedTrait === i ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.04)'}`,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    width: '100%',
                  }}
                >
                  <span style={{ color: 'rgba(201,168,76,0.4)', fontSize: 10, flexShrink: 0, marginTop: 2 }}>◈</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#d4c5a9', fontSize: 11, lineHeight: 1.5, marginBottom: expandedTrait === i ? 4 : 0 }}>
                      {trait.english}
                    </p>
                    {expandedTrait === i && (
                      <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(201,168,76,0.6)', direction: 'rtl', lineHeight: 1.6 }}>
                        {trait.arabic}
                      </p>
                    )}
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.3)', flexShrink: 0 }}>{trait.ref}</span>
                </button>
              ))}
            </div>
            <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10, lineHeight: 1.6, marginTop: 12, fontStyle: 'italic' }}>
              You are first a servant of Ar-Rahman. Your lineage is your mode of service.
            </p>
          </div>
        )}

        {/* Sunnah Prompts */}
        {activeTab === 'sunnah' && (
          <div>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6, marginBottom: 14, fontStyle: 'italic' }}>
              The Sunnah Module doesn&apos;t teach the Sunnah about — it activates the Sunnah through.
            </p>
            {activeSunnah && (
              <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 10, background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 6 }}>
                  Active Module Sunnah
                </p>
                <p style={{ color: '#d4c5a9', fontSize: 12, lineHeight: 1.7, marginBottom: 6, fontStyle: 'italic' }}>
                  &ldquo;{activeSunnah.hadith}&rdquo; ({activeSunnah.source})
                </p>
                <p style={{ color: 'rgba(201,168,76,0.6)', fontSize: 11 }}>{activeSunnah.reflection}</p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(SUNNAH_PROMPTS).map(([moduleId, prompt]) => (
                <div
                  key={moduleId}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.01)',
                    border: '1px solid rgba(255,255,255,0.04)',
                  }}
                >
                  <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.35)', marginBottom: 4 }}>
                    {moduleId.replace('-', ' ')}
                  </p>
                  <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic', marginBottom: 4 }}>
                    &ldquo;{prompt.hadith}&rdquo;
                  </p>
                  <p style={{ color: 'rgba(201,168,76,0.5)', fontSize: 10 }}>{prompt.reflection}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes sunnahPulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(201,168,76,0.5); }
          50% { opacity: 0.6; box-shadow: 0 0 16px rgba(201,168,76,0.8); }
        }
      `}</style>
    </div>
  );
}

// ─── SUNNAH HOVER PROMPT (for module tiles) ───────────────────────────────────

interface SunnahHoverPromptProps {
  moduleId: string;
  visible: boolean;
}

export function SunnahHoverPrompt({ moduleId, visible }: SunnahHoverPromptProps) {
  const prompt = SUNNAH_PROMPTS[moduleId];
  if (!prompt || !visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: 8,
        zIndex: 300,
        width: 240,
        background: '#0a1218',
        border: '1px solid rgba(201,168,76,0.25)',
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        animation: 'sunnahPromptIn 0.2s ease',
        pointerEvents: 'none',
      }}
    >
      <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10, lineHeight: 1.6, fontStyle: 'italic', marginBottom: 4 }}>
        The Prophet ﷺ said: &ldquo;{prompt.hadith}&rdquo;
      </p>
      <p style={{ color: 'rgba(201,168,76,0.5)', fontSize: 9 }}>{prompt.reflection}</p>
      <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid rgba(201,168,76,0.25)', margin: '6px auto 0' }} />
      <style>{`
        @keyframes sunnahPromptIn {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}
